import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import "./GovernmentPortal.css";

const fmt = (d) => d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-";
const money = (v) => `INR ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const GovernmentPortal = () => {
  const navigate = useNavigate();
  const [vendorPayments, setVendorPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeVendorPayment, setActiveVendorPayment] = useState(null);
  const [vendorPaymentForm, setVendorPaymentForm] = useState({ personName: "", amount: "", productQuantity: "", thumbDetails: "", remarks: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const vendorPaymentRes = await API.get("/government/vendor-payments");
      setVendorPayments(Array.isArray(vendorPaymentRes.data) ? vendorPaymentRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load Paddy thumb allotments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 15000);
    return () => window.clearInterval(timer);
  }, []);

  const vendorPaymentStats = useMemo(() => ({
    total: vendorPayments.length,
    allotted: vendorPayments.reduce((sum, r) => sum + Number(r.allottedAmount || r.amount || 0), 0),
    used: vendorPayments.reduce((sum, r) => sum + Number(r.usedAmount || r.paidAmount || 0), 0),
    paid: vendorPayments.filter((r) => r.status === "paid").length,
    remaining: vendorPayments.reduce((sum, r) => sum + Number(r.remainingAmount || 0), 0),
  }), [vendorPayments]);

  const briefReport = useMemo(() => {
    const vendorMap = new Map();
    vendorPayments.forEach((row) => {
      const key = row.vendor?._id || row.vendor?.companyName || row._id;
      const current = vendorMap.get(key) || {
        vendorName: row.vendor?.companyName || row.vendor?.userId?.fullName || "Vendor",
        assigned: 0,
        paid: 0,
        remaining: 0,
        count: 0,
      };
      current.assigned += Number(row.amount || 0);
      current.paid += Number(row.paidAmount || 0);
      current.remaining += Number(row.remainingAmount || 0);
      current.count += 1;
      vendorMap.set(key, current);
    });

    const validEntries = vendorPayments.flatMap((row) => (row.history || []).filter((entry) => entry.verificationStatus !== "vendor-disputed"));
    const totalQuantity = validEntries.reduce((sum, entry) => sum + Number(entry.productQuantity || 0), 0);
    const thumbEntries = validEntries.length;

    return {
      vendors: Array.from(vendorMap.values()),
      farmerThumbs: thumbEntries,
      farmerPending: vendorPayments.filter((row) => row.status !== "paid").length,
      quantityText: `${Number(totalQuantity).toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg`,
    };
  }, [vendorPayments]);

  const openVendorPayment = (row) => {
    setActiveVendorPayment(row);
    setVendorPaymentForm({ personName: "", amount: "", productQuantity: "", thumbDetails: "", remarks: "" });
  };

  const submitVendorPayment = async (e) => {
    e.preventDefault();
    if (!activeVendorPayment?._id) return;
    setSaving(true);
    try {
      await API.patch(`/government/vendor-payments/${activeVendorPayment._id}/payment`, vendorPaymentForm);
      setActiveVendorPayment(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update vendor payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gov-page">
      <section className="gov-hero">
        <div>
          <span className="gov-kicker">Paddy Thumb Desk</span>
          <h1>Vendor Paddy Thumb Verification</h1>
          <p>Accounts ke diye vendor names select karein, Paddy quantity bharein aur ₹24.71/kg par thumb detail submit karein.</p>
        </div>
        <div className="gov-stat-row">
          <div><strong>{vendorPaymentStats.total}</strong><span>Allotments</span></div>
          <div><strong>{vendorPaymentStats.paid}</strong><span>Completed</span></div>
          <div><strong>{briefReport.quantityText}</strong><span>Paddy</span></div>
        </div>
      </section>

      {error && <div className="gov-error">{error}</div>}

      <section className="admin-gov-card gov-supply-section gov-vendor-payment-desk">
        <div className="gov-section-head">
          <div>
            <h2>Assigned Vendor Payments</h2>
            <p>Vendor ke alag jama cash ka live Paddy Thumb ledger. Ye Vendor Panel Security se linked nahi hai.</p>
          </div>
          <div className="gov-stat-row gov-stat-row-light">
            <div><strong>{money(vendorPaymentStats.allotted)}</strong><span>Allotted</span></div>
            <div><strong>{money(vendorPaymentStats.used)}</strong><span>Used</span></div>
            <div><strong>{money(vendorPaymentStats.remaining)}</strong><span>Remaining</span></div>
          </div>
        </div>
        <button className="gov-primary gov-history-link" type="button" onClick={() => navigate('/government/thumb-history')}>
          <span className="material-symbols-outlined">history</span> View Complete Thumb History
        </button>
        {loading ? (
          <div className="gov-empty">Loading vendor payments...</div>
        ) : vendorPayments.length === 0 ? (
          <div className="gov-empty">No vendor payment assigned yet.</div>
        ) : (
          <div className="gov-dispatch-grid">
            {vendorPayments.map((row) => (
              <article className="gov-dispatch-card" key={row._id}>
                <div className="gov-card-top">
                  <div>
                    <button className="gov-vendor-history-link" type="button" onClick={() => navigate(`/government/vendors/${row.vendor?._id}/history`)}><h3>{row.vendor?.companyName || row.vendor?.userId?.fullName || "Vendor"}</h3><span className="material-symbols-outlined">arrow_forward</span></button>
                    <p>{row.vendor?.userId?.mobile || row.vendor?.contactMobile || "No mobile"}</p>
                  </div>
                  <span className={`gov-pill ${row.status || "assigned"}`}>{row.status || "assigned"}</span>
                </div>
                <div className="gov-info-grid">
                  <span>Thumb Allotted <b>{money(row.allottedAmount || row.amount)}</b></span>
                  <span>Thumb Used <b>{money(row.usedAmount || row.paidAmount)}</b></span>
                  <span>Remaining <b>{money(row.remainingAmount)}</b></span>
                  <span>Due <b>{fmt(row.dueDate)}</b></span>
                  <span>Area <b>{row.area || "-"}</b></span>
                  <span>Last Paid <b>{fmt(row.lastPaidAt)}</b></span>
                </div>
                <div className="gov-note">{row.instructions || "Vendor ko payment karke amount update karein."}</div>
                <button className="gov-secondary-action" type="button" onClick={() => navigate(`/government/vendors/${row.vendor?._id}/history`)}><span className="material-symbols-outlined">manage_search</span>View Vendor Complete History</button>
                <div className="gov-thumb-breakdown">
                  <div className="gov-thumb-breakdown-head"><b>Thumb Details</b><span>{(row.history || []).filter((entry) => entry.verificationStatus !== "vendor-disputed").length} entries</span></div>
                  {(row.history || []).length === 0 ? <p className="gov-thumb-empty">Abhi kisi beneficiary ka thumb submit nahi hua.</p> : [...row.history].reverse().map((entry, index) => <div className={`gov-thumb-person ${entry.verificationStatus === "vendor-disputed" ? "disputed" : ""}`} key={entry._id || index}>
                    <span className="gov-thumb-avatar">{String(entry.personName || "?").charAt(0).toUpperCase()}</span>
                    <div><b>{entry.personName || "Naam purane record me nahi hai"}</b><small>{fmt(entry.paidAt)} · {entry.verificationStatus || "submitted"}</small></div>
                    <div className="gov-thumb-values"><b>{money(entry.amount)}</b><small>{Number(entry.productQuantity || 0).toLocaleString("en-IN", { maximumFractionDigits: 4 })} kg</small></div>
                  </div>)}
                </div>
                {row.status !== "paid" && <button className="gov-primary" onClick={() => openVendorPayment(row)}>
                  <span className="material-symbols-outlined">payments</span>
                  Add Thumb Details
                </button>}
                {row.status === "paid" && <div className="gov-allotment-complete"><span className="material-symbols-outlined">check_circle</span>Allotment Fully Used</div>}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="admin-gov-card gov-supply-section">
        <div className="gov-section-head">
          <div>
            <h2>Brief Report</h2>
            <p>Vendor-wise amount, remaining, farmer thumb impression aur quantity ka quick summary.</p>
          </div>
          <div className="gov-stat-row gov-stat-row-light">
            <div><strong>{briefReport.farmerThumbs}</strong><span>Thumbs</span></div>
            <div><strong>{briefReport.farmerPending}</strong><span>Pending</span></div>
            <div><strong>{briefReport.quantityText}</strong><span>Quantity</span></div>
          </div>
        </div>
        <div className="admin-gov-table-wrap">
          <table className="admin-gov-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Assignments</th>
                <th>Assigned Amount</th>
                <th>Amount Given</th>
                <th>Vendor Payable</th>
                <th>Remaining To Give</th>
              </tr>
            </thead>
            <tbody>
              {briefReport.vendors.length === 0 ? (
                <tr><td colSpan="6">No vendor payment report yet.</td></tr>
              ) : briefReport.vendors.map((row) => (
                <tr key={row.vendorName}>
                  <td>{row.vendorName}</td>
                  <td>{row.count}</td>
                  <td>{money(row.assigned)}</td>
                  <td>{money(row.paid)}</td>
                  <td>{money(row.paid)}</td>
                  <td>{money(row.remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {activeVendorPayment && (
        <div className="gov-modal-backdrop">
          <form className="gov-modal" onSubmit={submitVendorPayment}>
            <div className="gov-modal-head">
              <h2>Submit Thumb Impression Details</h2>
              <button type="button" onClick={() => setActiveVendorPayment(null)}>×</button>
            </div>
            <div className="gov-info-grid">
              <span>Total <b>{money(activeVendorPayment.amount)}</b></span>
              <span>Remaining <b>{money(activeVendorPayment.remainingAmount)}</b></span>
              <span>Product <b>Paddy</b></span>
              <span>Rate <b>₹24.71/kg</b></span>
            </div>
            <label>Vendor dwara diya gaya naam</label>
            <select value={vendorPaymentForm.personName} onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, personName: e.target.value })} required>
              <option value="">Naam select karein</option>
              {(activeVendorPayment.beneficiaryNames || []).map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            {!(activeVendorPayment.beneficiaryNames || []).length && <div className="gov-profile-message error">Accounts ne vendor ke naam abhi update nahi kiye hain.</div>}
            <label>Amount (₹)</label>
            <input type="number" min="0.01" max={Number(activeVendorPayment.remainingAmount || 0)} step="0.01" value={vendorPaymentForm.amount} onChange={(e) => { const amount = e.target.value; const quantity = Number(amount) > 0 ? (Number(amount) / 24.71).toFixed(4) : ""; setVendorPaymentForm({ ...vendorPaymentForm, amount, productQuantity: quantity }); }} placeholder="Amount enter karein" required />
            <label>Calculated Paddy Quantity (kg)</label>
            <input value={vendorPaymentForm.productQuantity ? `${vendorPaymentForm.productQuantity} kg` : "Amount dalne par quantity aayegi"} readOnly />
            <div className="gov-note">Calculation: {money(vendorPaymentForm.amount)} ÷ ₹24.71 = <b>{vendorPaymentForm.productQuantity || "0"} kg Paddy</b></div>
            <label>Thumb Details</label>
            <textarea rows={3} value={vendorPaymentForm.thumbDetails} onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, thumbDetails: e.target.value })} />
            <label>Remarks</label>
            <textarea rows={3} value={vendorPaymentForm.remarks} onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, remarks: e.target.value })} />
            <button className="gov-primary" type="submit" disabled={saving || !(activeVendorPayment.beneficiaryNames || []).length}>{saving ? "Saving..." : "Submit Thumb Details"}</button>
          </form>
        </div>
      )}

    </div>
  );
};

export default GovernmentPortal;
